import React from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Block } from './Block';
import { computeBlockColor } from './colorUtils';

interface BlockLineProps {
  blocks: number;
  color: string;
  label?: string;
  index?: number;
  onAdd?: () => void;
  onRemove?: () => void;
  hideRemove?: boolean; // Whether to hide the remove button
  interactive?: boolean;
  /**
   * Highlight this line's add ('right') or remove ('left') edge. Routed to whichever block owns
   * that edge: removing happens on the first block, adding on the last.
   */
  pulse?: 'left' | 'right';
}

export const BlockLine = React.forwardRef<HTMLDivElement, BlockLineProps>(
  ({ blocks, color, label, index = 0, onAdd, onRemove, hideRemove = false, interactive = true, pulse }, ref) => {
    // Compute the effective color based on the label
    // If label contains a color word, use that color; otherwise use the default color
    const { color: effectiveColor, isLight } = computeBlockColor(label, color);

    return (
      <motion.div
        ref={ref}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -20 }}
        transition={{ duration: 0.3, ease: 'easeOut', delay: index * 0.1 }}
        className="tw-flex tw-gap-2"
      >
        <AnimatePresence mode="popLayout">
          {Array.from({ length: blocks }, (_, i) => (
            <Block
              key={i}
              color={effectiveColor}
              isLight={isLight}
              index={i}
              isFirst={i === 0}
              isLast={i === blocks - 1}
              onAdd={i === blocks - 1 ? onAdd : undefined}
              onRemove={i === 0 ? onRemove : undefined}
              hideRemove={hideRemove}
              interactive={interactive}
              pulse={(pulse === 'left' && i === 0) || (pulse === 'right' && i === blocks - 1) ? pulse : undefined}
            />
          ))}
        </AnimatePresence>
      </motion.div>
    );
  },
);

BlockLine.displayName = 'BlockLine';
